import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Users, BookOpen, Calendar, Briefcase, Monitor, Heart, ArrowRight, Globe, Camera, Activity, CheckCircle2, ChevronDown } from 'lucide-react';
const fadeIn = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-100px'
  },
  transition: {
    duration: 0.6
  }
};
export function Volunteer() {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const roles = [{
    title: 'Education Mentor',
    icon: BookOpen,
    time: 'Flexible hrs/wk',
    desc: 'Teach and mentor youth in academic subjects, coding, or cultural arts.'
  }, {
    title: 'Event Volunteer',
    icon: Calendar,
    time: 'Event Dates',
    desc: 'Help organize and manage community events, festivals, and workshops.'
  }, {
    title: 'Business Mentor',
    icon: Briefcase,
    time: '2-4 hrs/mo',
    desc: 'Guide entrepreneurs and young professionals in scaling their ideas.'
  }, {
    title: 'Community Outreach',
    icon: Globe,
    time: 'Flexible',
    desc: 'Help with scholarship programs and community engagement.'
  }, {
    title: 'Tech & Social Media',
    icon: Monitor,
    time: 'Flexible',
    desc: 'Support our digital presence, website, and content creation.'
  }, {
    title: 'Content Creator',
    icon: Camera,
    time: 'Flexible',
    desc: 'Create stories, videos, and resources for our platform.'
  }, {
    title: 'Health Advocate',
    icon: Activity,
    time: 'Flexible',
    desc: 'Support health awareness programs (Health background preferred).'
  }];
  const faqs = [{
    q: 'Do I need prior experience?',
    a: 'While some specialized roles (like Health Advocate) prefer experience, many of our roles only require passion, reliability, and a willingness to learn. We provide comprehensive training.'
  }, {
    q: 'How much time do I need to commit?',
    a: 'Commitment levels vary by role. Some require just a few hours a month, while others might need a weekly commitment. We offer flexible scheduling to fit your life.'
  }, {
    q: 'Can I volunteer remotely?',
    a: 'Yes! Roles like Tech & Social Media, Content Creator, and Business Mentor can often be done entirely remotely from anywhere in the world.'
  }, {
    q: 'What\'s the application process?',
    a: 'Simply fill out the application form below. Our team will review your submission and contact you within 3-5 business days to schedule a brief introductory call.'
  }, {
    q: 'Can I volunteer with my organization?',
    a: 'Absolutely. We welcome corporate groups, student organizations, and community groups. Please indicate this on your application or contact us directly.'
  }];
  return <div className="pt-0 bg-warmwhite" data-id="element-1352" data-mp="2yh9rwfafmhjp">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-navy overflow-hidden" data-id="element-1353" data-mp="2odmh8ig9jhvx">
        <div className="absolute inset-0 z-0" data-id="element-1354" data-mp="2obi19253asfa">
          <img src="https://images.unsplash.com/photo-1559027615-cd4628902d4a?q=80&w=2940&auto=format&fit=crop" alt="Volunteer" className="w-full h-full object-cover opacity-30" data-id="element-1355" data-mp="2tbwauomcaaff" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/80 to-transparent" data-id="element-1356" data-mp="333adh59l7qcs" />
        </div>
        <div className="container mx-auto px-6 md:px-12 relative z-10 text-center max-w-4xl" data-id="element-1357" data-mp="373udqhpgja35">
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-white mb-6" data-id="element-1358" data-mp="3c1m6tf1u928x">
            Be a Change Maker
          </h1>
          <p className="text-xl text-white/80 leading-relaxed mb-8" data-id="element-1359" data-mp="3h0ff8t29q328">
            Join our volunteer community and make a direct impact. Whether you
            have skills, passion, or just time, we need you.
          </p>
          <button onClick={() => {
          document.getElementById('application-form')?.scrollIntoView({
            behavior: 'smooth'
          });
        }} className="px-8 py-4 bg-gold hover:bg-gold-light text-white rounded-full font-bold transition-all shadow-lg" data-id="element-1360" data-mp="1rmtvb46nevm8">
            Apply to Volunteer
          </button>
        </div>
      </section>

      {/* Why Join Us */}
      <section className="py-12 lg:py-16" data-id="element-1361" data-mp="3m5j39fdb26q9">
        <div className="container mx-auto px-6 md:px-12" data-id="element-1362" data-mp="3m6kh1rlph7bo">
          <div className="flex flex-col lg:flex-row gap-16 items-center" data-id="element-1363" data-mp="2yfyeugyduh9l">
            <motion.div variants={fadeIn} initial="initial" whileInView="whileInView" className="lg:w-1/2" data-id="element-1364" data-mp="2uev428v6zbf8">
              <h2 className="text-4xl font-serif font-bold text-navy mb-8" data-id="element-1365" data-mp="388ckvlwhua1k">
                Why Join Us?
              </h2>
              <ul className="space-y-4 mb-10" data-id="element-1366" data-mp="2fpyu269krobz">
                {['Make a real difference in communities', 'Develop new skills and gain experience', 'Connect with like-minded, passionate people', 'Be part of a global movement', 'Flexible scheduling to fit your life', 'Comprehensive training & support provided'].map((item, i) => <li key={i} className="flex items-center gap-3 text-mediumgray text-lg" data-id="element-1367" data-mp="h9ztjft8199z" data-mpi={i}>
                    <Heart className="w-5 h-5 text-gold shrink-0" data-id="element-1368" data-mp="19watco90765q" /> {item}
                  </li>)}
              </ul>
              <div className="bg-white p-6 rounded-2xl border-l-4 border-gold shadow-sm" data-id="element-1369" data-mp="1dx4itm23gt0q">
                <p className="text-mediumgray italic mb-4" data-id="element-1370" data-mp="zxlbuwl0rnq0">
                  "Volunteering with Poch Global has been the most rewarding
                  experience. Seeing the direct impact of our work on the youth
                  is truly inspiring."
                </p>
                <p className="font-bold text-navy text-sm" data-id="element-1371" data-mp="3pohvr0whdulo">
                  — Sarah J., Education Mentor
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeIn} initial="initial" whileInView="whileInView" className="lg:w-1/2 grid grid-cols-2 gap-4" data-id="element-1372" data-mp="35nondcz705op">
              <img src="https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?q=80&w=2940&auto=format&fit=crop" alt="Volunteers" className="w-full h-64 object-cover rounded-3xl rounded-tr-none" data-id="element-1373" data-mp="2wr7e6fae2hvl" />
              <img src="https://images.unsplash.com/photo-1528301721190-186c3bd85418?q=80&w=2940&auto=format&fit=crop" alt="Community" className="w-full h-64 object-cover rounded-3xl rounded-bl-none mt-8" data-id="element-1374" data-mp="2huq21ludkz7z" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Roles */}
      <section className="py-12 lg:py-16 bg-white border-y border-softgray" data-id="element-1375" data-mp="73otjacao435">
        <div className="container mx-auto px-6 md:px-12" data-id="element-1376" data-mp="enr13g5p1jyq">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1377" data-mp="en7swr5ui9xw">
            <h2 className="text-4xl font-serif font-bold text-navy mb-4" data-id="element-1378" data-mp="jmaqxrnerfwd">
              Volunteer Roles
            </h2>
            <p className="text-mediumgray text-lg" data-id="element-1379" data-mp="oevewcjmuyrz">
              Find the perfect opportunity to share your skills and passion.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1380" data-mp="3ruiyowsu7xk3">
            {roles.map((role, i) => <motion.div key={i} variants={fadeIn} initial="initial" whileInView="whileInView" className="bg-warmwhite p-8 rounded-3xl border border-softgray hover:border-gold/30 transition-colors group" data-id="element-1381" data-mp="ng8a7ktpvax" data-mpi={i}>
                <div className="flex items-start gap-6" data-id="element-1382" data-mp="1h2r8h8dwoakj">
                  <div className="w-14 h-14 bg-white text-gold rounded-2xl flex items-center justify-center shrink-0 shadow-sm" data-id="element-1383" data-mp="1h4aqp4j4gvoy">
                    <role.icon className="w-7 h-7" data-id="element-1384" data-mp="2753lwz080cxj" />
                  </div>
                  <div data-id="element-1385" data-mp="2730m5n99n5fm">
                    <h3 className="text-xl font-bold text-navy mb-2" data-id="element-1386" data-mp="226ilozkyxt93">
                      {role.title}
                    </h3>
                    <p className="text-sm font-semibold text-gold mb-3 uppercase tracking-wider" data-id="element-1387" data-mp="2gw7y5qyihjoo">
                      Time: {role.time}
                    </p>
                    <p className="text-mediumgray mb-6" data-id="element-1388" data-mp="2pvkztxge93yu">{role.desc}</p>
                    <button onClick={() => {
                  document.getElementById('application-form')?.scrollIntoView({
                    behavior: 'smooth'
                  });
                }} className="text-navy font-bold flex items-center gap-2 group-hover:text-gold transition-colors" data-id="element-1389" data-mp="2pxxsb4js2wq5">
                      Apply Now <ArrowRight className="w-4 h-4" data-id="element-1390" data-mp="15f7zmdlkm8m7" />
                    </button>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Volunteer Application Form */}
      <section id="application-form" className="py-12 lg:py-16 bg-warmwhite" data-id="element-1391" data-mp="3c6cvg68crtfp">
        <div className="container mx-auto px-6 md:px-12 max-w-4xl" data-id="element-1392" data-mp="3h2ti0vzwlzqo">
          <div className="bg-white p-8 md:p-12 rounded-3xl shadow-premium border border-softgray" data-id="element-1393" data-mp="32f8g35erm6kc">
            <div className="text-center mb-10" data-id="element-1394" data-mp="37ci54jy8sg7n">
              <h2 className="text-3xl font-serif font-bold text-navy mb-4" data-id="element-1395" data-mp="37b71ql8plah2">Ready to Volunteer?</h2>
              <p className="text-mediumgray" data-id="element-1396" data-mp="2tg4zq96zg59j">Fill out the form below and we'll review and contact you within 3-5 business days.</p>
            </div>

            <form className="space-y-6" data-id="element-1397" data-mp="2jgmhpyamutk4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1398" data-mp="2jhp8m67ygw01">
                <div data-id="element-1399" data-mp="2ofh04z4oug3p">
                  <label className="block text-sm font-bold text-navy mb-2" data-id="element-1400" data-mp="2oexou19jmpyn">Full Name *</label>
                  <input required type="text" className="w-full px-4 py-3 rounded-xl border border-softgray focus:outline-none focus:border-navy bg-warmwhite" data-id="element-1401" data-mp="hp0yre0t3aba" />
                </div>
                <div data-id="element-1402" data-mp="1h9qefme7sbnv">
                  <label className="block text-sm font-bold text-navy mb-2" data-id="element-1403" data-mp="1h8f75clk8ls0">Email *</label>
                  <input required type="email" className="w-full px-4 py-3 rounded-xl border border-softgray focus:outline-none focus:border-navy bg-warmwhite" data-id="element-1404" data-mp="1m2jhm5u1bbxu" />
                </div>
                <div data-id="element-1405" data-mp="1d9puxnc9da9y">
                  <label className="block text-sm font-bold text-navy mb-2" data-id="element-1406" data-mp="1d6u0i8zmd3q9">Phone *</label>
                  <input required type="tel" className="w-full px-4 py-3 rounded-xl border border-softgray focus:outline-none focus:border-navy bg-warmwhite" data-id="element-1407" data-mp="20zagmdzsqlmx" />
                </div>
                <div data-id="element-1408" data-mp="1qzjnafmz2cof">
                  <label className="block text-sm font-bold text-navy mb-2" data-id="element-1409" data-mp="1r2fku2uwqzcc">Location / Remote Preference</label>
                  <select className="w-full px-4 py-3 rounded-xl border border-softgray focus:outline-none focus:border-navy bg-warmwhite text-mediumgray" data-id="element-1410" data-mp="1w18zhylxkwe3">
                    <option data-id="element-1411" data-mp="kl27pm7y8atr">In-person (New York)</option>
                    <option data-id="element-1412" data-mp="d0qajtxf2pt0">Remote / Virtual</option>
                    <option data-id="element-1413" data-mp="cz6plq0tfqkw">Flexible</option>
                  </select>
                </div>
                <div className="md:col-span-2" data-id="element-1414" data-mp="80vhpku2zq5b">
                  <label className="block text-sm font-bold text-navy mb-2" data-id="element-1415" data-mp="m1fy6acix9vn">Volunteer Role Interested In *</label>
                  <select required className="w-full px-4 py-3 rounded-xl border border-softgray focus:outline-none focus:border-navy bg-warmwhite text-mediumgray" data-id="element-1416" data-mp="h1tno8o2y7qj">
                    <option value="" data-id="element-1417" data-mp="3pg264nww1i4z">Select a role...</option>
                    {roles.map(r => <option key={r.title} value={r.title} data-id="element-1418" data-mp="3li4ktw68ng6v">{r.title}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2" data-id="element-1419" data-mp="34euqtymbhtt">
                  <label className="block text-sm font-bold text-navy mb-2" data-id="element-1420" data-mp="3ug6rar8qr11j">Experience / Background</label>
                  <textarea rows={3} className="w-full px-4 py-3 rounded-xl border border-softgray focus:outline-none focus:border-navy bg-warmwhite" data-id="element-1421" data-mp="36qcgef7zfmjx"></textarea>
                </div>
                <div className="md:col-span-2" data-id="element-1422" data-mp="15q7hqlcadxh7">
                  <label className="block text-sm font-bold text-navy mb-2" data-id="element-1423" data-mp="15nbjevwj16s8">Skills You Have</label>
                  <input type="text" placeholder="e.g., Graphic Design, Teaching, Event Planning" className="w-full px-4 py-3 rounded-xl border border-softgray focus:outline-none focus:border-navy bg-warmwhite" data-id="element-1424" data-mp="1amfrfj7psygz" />
                </div>
                <div data-id="element-1425" data-mp="mqtne2anakq6">
                  <label className="block text-sm font-bold text-navy mb-2" data-id="element-1426" data-mp="mrlilc0bygfx">Availability</label>
                  <input type="text" placeholder="e.g., Weekends, Evenings" className="w-full px-4 py-3 rounded-xl border border-softgray focus:outline-none focus:border-navy bg-warmwhite" data-id="element-1427" data-mp="rqppfvzs8233" />
                </div>
                <div data-id="element-1428" data-mp="10r4dv481lxat">
                  <label className="block text-sm font-bold text-navy mb-2" data-id="element-1429" data-mp="10rnwyo9firqf">Commitment Level</label>
                  <select className="w-full px-4 py-3 rounded-xl border border-softgray focus:outline-none focus:border-navy bg-warmwhite text-mediumgray" data-id="element-1430" data-mp="283mtslqd1143">
                    <option data-id="element-1431" data-mp="ieesl3awvt8">1-5 hours/month</option>
                    <option data-id="element-1432" data-mp="k9389ds7622">5-10 hours/month</option>
                    <option data-id="element-1433" data-mp="3sn04moh04xmr">10+ hours/month</option>
                    <option data-id="element-1434" data-mp="3sobaquxx6hh1">Event-based only</option>
                  </select>
                </div>
                <div className="md:col-span-2" data-id="element-1435" data-mp="3nsvy26bnerm7">
                  <label className="block text-sm font-bold text-navy mb-2" data-id="element-1436" data-mp="3iv2uzm5n6dq5">Why do you want to volunteer with us? *</label>
                  <textarea required rows={4} className="w-full px-4 py-3 rounded-xl border border-softgray focus:outline-none focus:border-navy bg-warmwhite" data-id="element-1437" data-mp="3dycdqvi3pc35"></textarea>
                </div>
              </div>
              <button type="submit" className="w-full py-4 bg-navy hover:bg-navy-light text-white rounded-xl font-bold transition-all shadow-md mt-6" data-id="element-1438" data-mp="34ygxic9ouqw9">
                Submit Application
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Requirements & Support (Split Section) */}
      <section className="py-12 lg:py-16 bg-white border-y border-softgray" data-id="element-1439" data-mp="3r0dkbivp9d3f">
        <div className="container mx-auto px-6 md:px-12" data-id="element-1440" data-mp="3qyajs4wx81kg">
          <div className="flex flex-col md:flex-row gap-16" data-id="element-1441" data-mp="3vyf5c9cormhj">
            <motion.div variants={fadeIn} initial="initial" whileInView="whileInView" className="md:w-1/2" data-id="element-1442" data-mp="3hzp4qjqnr4of">
              <h2 className="text-3xl font-serif font-bold text-navy mb-8" data-id="element-1443" data-mp="3myjrfwrqxddt">What We Expect</h2>
              <ul className="space-y-4" data-id="element-1444" data-mp="38a5m9f6uxcu3">
                {['Commitment to Poch Global\'s mission', 'Reliability and punctuality', 'Respectful and inclusive behavior', 'Willingness to learn and adapt', 'Passion for community impact'].map((item, i) => <li key={i} className="flex items-center gap-3 text-mediumgray" data-id="element-1445" data-mp="33b2kq4q35oqv" data-mpi={i}>
                    <div className="w-2 h-2 rounded-full bg-gold shrink-0" data-id="element-1446" data-mp="2pbjkdy3inhp4" /> {item}
                  </li>)}
              </ul>
            </motion.div>
            <motion.div variants={fadeIn} initial="initial" whileInView="whileInView" className="md:w-1/2" data-id="element-1447" data-mp="2anj8x95o0qgj">
              <h2 className="text-3xl font-serif font-bold text-navy mb-8" data-id="element-1448" data-mp="25qb1a2i584tj">We Support Our Volunteers</h2>
              <ul className="space-y-4" data-id="element-1449" data-mp="2knjt0xbczals">
                {['Comprehensive training programs', 'Ongoing mentorship and guidance', 'Access to resources & materials', 'A vibrant community of volunteers', 'Recognition & official certificates', 'Flexible scheduling options'].map((item, i) => <li key={i} className="flex items-center gap-3 text-mediumgray" data-id="element-1450" data-mp="33cgnp2ao454j" data-mpi={i}>
                    <CheckCircle2 className="w-5 h-5 text-gold shrink-0" data-id="element-1451" data-mp="2yf873656495e" /> {item}
                  </li>)}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Volunteer Stories */}
      <section className="py-12 lg:py-16 bg-navy text-white relative overflow-hidden" data-id="element-1452" data-mp="38a9x0479pf91">
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')]" data-id="element-1453" data-mp="3fd6nv0xqhg9l" />
        <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-1454" data-mp="3pdgyqewaqbll">
          <div className="flex justify-between items-end mb-16" data-id="element-1455" data-mp="3pbe0x8owje6b">
            <div data-id="element-1456" data-mp="3u4z2tbtm8sas">
              <h2 className="text-4xl text-white font-serif font-bold mb-4" data-id="element-1457" data-mp="3u5i9fwdtfu9i">Meet Our Volunteers</h2>
              <p className="text-white/70 text-lg" data-id="element-1458" data-mp="204xr9wbl92w">Hear directly from the people making a difference.</p>
            </div>
            <button className="hidden md:flex items-center gap-2 text-gold font-bold hover:text-gold-light transition-colors" data-id="element-1459" data-mp="71atzhp6veh6">
              View All Stories <ArrowRight className="w-4 h-4" data-id="element-1460" data-mp="gwm8q00lklox" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1461" data-mp="2dqgz8j5s3m5l">
            {[{
            name: 'Marcus T.',
            role: 'Tech Mentor',
            img: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=2787&auto=format&fit=crop',
            quote: 'Teaching coding to these bright kids has been the highlight of my year. Their eagerness to learn is unmatched.'
          }, {
            name: 'Elena R.',
            role: 'Event Coordinator',
            img: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=2788&auto=format&fit=crop',
            quote: 'Being part of the cultural festival organizing team allowed me to connect deeply with my roots while giving back.'
          }, {
            name: 'James L.',
            role: 'Business Advisor',
            img: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?q=80&w=2787&auto=format&fit=crop',
            quote: 'Mentoring young entrepreneurs through Poch Global has shown me the incredible untapped potential in our community.'
          }].map((story, i) => <motion.div key={i} variants={fadeIn} initial="initial" whileInView="whileInView" className="bg-white/10 backdrop-blur-md p-8 rounded-3xl border border-white/20" data-id="element-1462" data-mp="3uib1qzxv0eh4" data-mpi={i}>
                <p className="text-white/90 italic mb-8" data-id="element-1463" data-mp="3plavop10fbq0">"{story.quote}"</p>
                <div className="flex items-center gap-4" data-id="element-1464" data-mp="1tfa4dfihrwow">
                  <img src={story.img} alt={story.name} className="w-12 h-12 rounded-full object-cover border-2 border-gold" data-id="element-1465" data-mp="1yewidr659gyr" />
                  <div data-id="element-1466" data-mp="1jiyt8sun1cw1">
                    <div className="font-bold text-white" data-id="element-1467" data-mp="1jjqeb9qlzs82">{story.name}</div>
                    <div className="text-xs text-gold uppercase tracking-wider" data-id="element-1468" data-mp="1ohrw7rcatlhy">{story.role}</div>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 lg:py-16 bg-warmwhite border-b border-softgray" data-id="element-1469" data-mp="2h7ibi3po2tfn">
        <div className="container mx-auto px-6 md:px-12 max-w-3xl" data-id="element-1470" data-mp="22ghwffuf01aj">
          <div className="text-center mb-16" data-id="element-1471" data-mp="27et69qkpmbsr">
            <h2 className="text-4xl font-serif font-bold text-navy mb-4" data-id="element-1472" data-mp="27di0jl7petz0">Volunteer FAQs</h2>
            <p className="text-lg text-mediumgray" data-id="element-1473" data-mp="2v3cey77o8qld">Common questions about volunteering with us.</p>
          </div>
          
          <div className="space-y-4" data-id="element-1474" data-mp="ogwwa6lf1mzx">
            {faqs.map((faq, i) => <div key={i} className="bg-white border border-softgray rounded-2xl overflow-hidden shadow-sm" data-id="element-1475" data-mp="oettsn32u1eb" data-mpi={i}>
                <button onClick={() => setActiveFaq(activeFaq === i ? null : i)} className="w-full px-6 py-5 flex items-center justify-between hover:bg-lightgray transition-colors text-left" data-id="element-1476" data-mp="y6zv4nf7xv66">
                  <span className="font-bold text-navy" data-id="element-1477" data-mp="5j3q8vfhwwyb">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-mediumgray transition-transform duration-300 shrink-0 ${activeFaq === i ? 'rotate-180' : ''}`} data-id="element-1478" data-mp="jr3eaomjpvq" />
                </button>
                <AnimatePresence data-id="element-1479" data-mp="ahezp51vp5g0">
                  {activeFaq === i && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden" data-id="element-1480" data-mp="aji0mk4kkizi">
                      <div className="px-6 pb-5 text-mediumgray border-t border-softgray pt-4 leading-relaxed" data-id="element-1481" data-mp="1aq74ci8j6t07">
                        {faq.a}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
      </section>

      {/* Other Ways to Help */}
      <section className="py-12 lg:py-16 bg-white text-center" data-id="element-1482" data-mp="1to1cyv5qz2i2">
        <div className="container mx-auto px-6" data-id="element-1483" data-mp="1tm6ppa706yat">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-navy mb-8" data-id="element-1484" data-mp="1plwla1yv0fy2">
            Other Ways to Help
          </h2>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-1485" data-mp="23dkrxpqcac7n">
            <Link to="/donate" className="px-8 py-4 bg-gold text-white hover:bg-gold-light rounded-full font-bold transition-all shadow-lg hover:-translate-y-1" data-id="element-1486" data-mp="1ygavhq4j90ab">
              Donate Now
            </Link>
            <Link to="/shop" className="px-8 py-4 bg-navy text-white hover:bg-navy-light rounded-full font-bold transition-all shadow-lg hover:-translate-y-1" data-id="element-1487" data-mp="28eqo5xj7khlx">
              Shop & Support
            </Link>
            <Link to="/contact" className="px-8 py-4 bg-transparent border-2 border-navy text-navy hover:bg-navy hover:text-white rounded-full font-bold transition-all hover:-translate-y-1" data-id="element-1488" data-mp="2vzj4wjhex5eb">
              Partner With Us
            </Link>
          </div>
        </div>
      </section>
    </div>;
}